/**
 * A seção «Tradução do Ler Texto» de Insights (TR1, 23/10/2026): quanto o
 * botão «Traduzir» do gerenciador está custando.
 *
 * Tudo chega somado do livro-razão `translation_usage_events`; aqui só se
 * formata. «Chamada» é uma ida ao motor: um clique que não saiu do cache vira
 * uma chamada ao GPT-5.4 mini, e mais uma ao Google quando cai na reserva. O
 * valor é de tabela, sem franquia — prévia da fatura, nunca a fatura.
 */
import type { InsightsRange, TranslationUsageSummary } from "./api";
import { formatUnits, RANGE_PHRASE } from "./ocrCost.ts";

export interface TranslationCostCard {
  key: "month" | "range" | "previous_month";
  label: string;
  value: string;
  subLabel: string;
}

const centesimos = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const milesimos = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

/**
 * `US$ 1,50`, e `US$ 0,0021` abaixo de um centavo: uma tradução custa
 * milésimos de dólar, e «US$ 0,00» diria que ela não custou nada.
 */
export function formatTranslationUsd(value: number): string {
  return value > 0 && value < 0.01 ? milesimos.format(value) : centesimos.format(value);
}

function chamadas(n: number): string {
  return n === 1 ? "1 chamada" : `${formatUnits(n)} chamadas`;
}

function caracteres(n: number): string {
  return n === 1 ? "1 caractere" : `${formatUnits(n)} caracteres`;
}

export function translationCostCards(t: TranslationUsageSummary, range: InsightsRange): TranslationCostCard[] {
  return [
    {
      key: "month",
      label: "Tradução no mês",
      value: formatTranslationUsd(t.month.cost_usd),
      subLabel: `${chamadas(t.month.calls)} · ${caracteres(t.month.chars)}`,
    },
    {
      key: "range",
      label: `Tradução ${RANGE_PHRASE[range]}`,
      value: formatTranslationUsd(t.period.cost_usd),
      subLabel: `${chamadas(t.period.calls)} · ${caracteres(t.period.chars)}`,
    },
    {
      key: "previous_month",
      label: "Tradução no mês anterior",
      value: formatTranslationUsd(t.previous_month.cost_usd),
      subLabel: chamadas(t.previous_month.calls),
    },
  ];
}

/** O motor com o nome da tela: o modelo, ou o Google quando foi a reserva. */
export function translationEngineLabel(engine: string): string {
  if (engine === "google-translate-v3") return "Google (reserva)";
  if (engine.startsWith("gpt-")) return `GPT-${engine.slice(4).replace(/-(mini|nano)$/, " $1")}`;
  return engine;
}

export function translationEngineColorClass(engine: string): string {
  return engine === "google-translate-v3" ? "bg-blue-400" : "bg-emerald-400";
}
